import React, { useRef, useState, useEffect } from 'react';
import { map, isEmpty } from 'lodash/fp';
import {
  Form,
  Schema,
  FormControl,
  ControlLabel,
  CheckPicker,
  Button,
  ButtonToolbar,
  Panel,
  Icon,
  Whisper,
  Tooltip
} from 'rsuite';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  InlineHelpBlock,
  InlineFormGroup
} from '../../../widgets/FormElements';
import {
  fetchInventoryLocationOptions,
  fetchLowStockItems
} from '../../../../redux/Inventory/sagas/routines';
import {
  resetLowStockItems,
  updateLowStockItemsSearchQuery
} from '../../../../redux/Inventory/actions/reports/low-stock-items';
import { getIsFetching } from '../../../../redux/Inventory/selectors/reports/low-stock-items';
import {
  getLocationOptions,
  getIsFetchingLocationOptions
} from '../../../../redux/Inventory/selectors/components/adjust-item-form';
import ReportTemplatesModal from '../../../../containers/ReportTemplatesModal';
import { LOW_STOCK_ITEMS } from '../../../../helpers/ReportTemplateTypes';

const StyledFormGroup = styled(InlineFormGroup)`
  margin-bottom: 0 !important;
`;

const { ArrayType } = Schema.Types;

const FormModel = Schema.Model({
  inventoryLocationId: ArrayType()
    .isRequired('This field is required')
    .minLength(1, 'Please select at least 1.')
});

export default () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [showPresetModal, setShowPresetModal] = useState(false);

  const isFetching = useSelector(getIsFetching);
  const locationOptions = useSelector(getLocationOptions);
  const isLoadingLocations = useSelector(getIsFetchingLocationOptions);

  const revalidateForm = () => {
    setTimeout(formRef.current.check, 1);
  };

  const setDefaultFormValue = () => {
    const inventoryLocationId = map('value', locationOptions);
    setFormValue({ inventoryLocationId });
  };

  useEffect(() => {
    dispatch(fetchInventoryLocationOptions());
  }, []);

  useEffect(
    () => {
      if (!isEmpty(locationOptions)) {
        setDefaultFormValue();
      }
    },
    [locationOptions]
  );

  const executeSearch = query => {
    dispatch(fetchLowStockItems(query));
    dispatch(updateLowStockItemsSearchQuery(query));
  };

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFormCheck = error => {
    setFormError(error);
  };

  const handleFormSubmit = () => {
    const noError = formRef.current.check();
    if (noError) {
      executeSearch(formValue);
    }
  };

  const handleFormReset = () => {
    setDefaultFormValue();
    revalidateForm();
    dispatch(resetLowStockItems());
  };

  const onHidePresetModal = () => {
    setShowPresetModal(false);
  };

  const handleTogglePresetModal = () => {
    const showModal = !showPresetModal;
    setShowPresetModal(showModal);
  };

  const handleSelectTemplate = ({ file: searchQuery }) => {
    onHidePresetModal();
    setFormValue(searchQuery);
    revalidateForm();
    executeSearch(searchQuery);
  };

  const disabledAction = isFetching || isLoadingLocations;

  return (
    <Panel bordered>
      <Form
        model={FormModel}
        ref={formRef}
        formValue={formValue}
        onChange={handleFormChange}
        onCheck={handleFormCheck}
      >
        <StyledFormGroup>
          <ControlLabel>
            Inventory Locations*
            <InlineHelpBlock tooltip>
              Select one or multiple locations.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 180 }}
            placeholder={
              isLoadingLocations ? 'Loading...' : 'Inventory Locations'
            }
            name="inventoryLocationId"
            accepter={CheckPicker}
            data={locationOptions}
            errorMessage={formError.inventoryLocationId}
          />
        </StyledFormGroup>
        <ButtonToolbar style={{ float: 'right', marginTop: 12 }}>
          <Button
            appearance="primary"
            onClick={handleFormSubmit}
            disabled={disabledAction}
          >
            <Icon icon="search" /> Search
          </Button>
          <Button
            appearance="default"
            onClick={handleFormReset}
            disabled={disabledAction}
          >
            <Icon icon="refresh" /> Reset
          </Button>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>View Presets</Tooltip>}
          >
            <Button
              appearance="primary"
              color="green"
              onClick={handleTogglePresetModal}
              disabled={disabledAction}
            >
              <Icon icon="list" /> Presets
            </Button>
          </Whisper>
        </ButtonToolbar>
      </Form>
      <ReportTemplatesModal
        show={showPresetModal}
        onHide={onHidePresetModal}
        onSelect={handleSelectTemplate}
        fileType={LOW_STOCK_ITEMS}
      />
    </Panel>
  );
};
